import { useUpdateTasks } from 'features/tasks';
import React, { useMemo, useRef } from 'react';
import { NewTaskRow } from 'shared/components/connected/new-task-row';
import {
  RenderItemProps,
  rootId,
  TreeItem,
  TreeItemTypes,
  useTree,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import { useTaskDateShortcuts } from 'shared/hooks/use-task-date-shortcuts';
import { useTaskPriorityShortcuts } from 'shared/hooks/use-task-priority-shortcuts';
import { ID } from 'shared/types/id';
import { goalInboxID } from 'shared/types/orderable-section';
import { Task } from 'shared/types/task';

import { useGoalTree } from '../../hooks/use-goal-tree';
import { GroupedListProps } from '../../types';
import { GoalHeader } from '../goal-header';
import { TreeTaskCard } from '../tree-task-card';
import * as Styled from './goal-grouping-tasks-list.style';

const goalDragTypes = [TreeItemTypes.Task, TreeItemTypes.Goal];
const taskTreeItemTypes = [TreeItemTypes.Task];

const dropTypes = {
  [TreeItemTypes.Root]: [TreeItemTypes.Goal],
  [TreeItemTypes.Goal]: [TreeItemTypes.Task],
  [TreeItemTypes.Task]: [],
  [TreeItemTypes.LifeArea]: [],
  [TreeItemTypes.Priority]: [],
  [TreeItemTypes.Date]: [],
  [TreeItemTypes.DayPart]: [],
  [TreeItemTypes.Tag]: [],
  [TreeItemTypes.Type]: [],
  [TreeItemTypes.NewTask]: [],
  [TreeItemTypes.Section]: [],
};

export const GoalGroupingTasksList: React.FC<GroupedListProps> = ({
  tasks,
  lifeAreas,
  goals,
  sortingMode,
  onReorder,
  customOrder = {},
  collapsedSections = [],
  onSectionCollapse,
  weekStartsOn,
  scrollElement,
  denseFormat,
  groupSettings,
  isContextMenuOpen,
  defaultNewTaskDate,
  canReorderGoals,
}) => {
  const { displayDue } = groupSettings;
  const today = useToday();

  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  const treeItems = useGoalTree({
    goals,
    tasks,
    customOrder,
    sortingMode,
    today,
    weekStartsOn,
    showEmptyGroups: groupSettings.showEmptyGroups,
    hideNoGoalGroup: groupSettings.hideNoGoalGroup,
  });

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }

    const updatedDocs: Task[] = [];
    const updatedSections: { id: ID; taskSorting: ID[] }[] = [];
    updatedItems.forEach((item) => {
      if (item.type === TreeItemTypes.Task) {
        if (!tasksMap[item.id]) {
          return;
        }
        const taskToUpdate = structuredClone(tasksMap[item.id]);
        const parentItem =
          updatedItems.find(({ children }) => children?.includes(item.id)) ??
          Object.values(treeItems).find(({ children }) =>
            children?.includes(item.id),
          );

        const goalId =
          !parentItem?.id || parentItem.id === goalInboxID
            ? null
            : parentItem.id;

        taskToUpdate.goalId = goalId;

        if (goalId) {
          taskToUpdate.lifeAreaId = null;
        }

        updatedDocs.push(taskToUpdate);
      }
      if (item.type === TreeItemTypes.Root) {
        updatedSections.push({
          id: rootId,
          taskSorting: item.children
            ? item.children.filter((childId) => !childId.endsWith('-new-task'))
            : [],
        });
      }
      if (item.type === TreeItemTypes.Goal) {
        updatedSections.push({
          id: item.id,
          taskSorting: item.children
            ? item.children.filter((childId) => !childId.endsWith('-new-task'))
            : [],
        });
      }
    });

    onReorder(updatedSections, updatedDocs);
  };

  // using refs to prevent the renderItem from rerendering all items
  const tasksMapRef = useRef(tasksMap);
  tasksMapRef.current = tasksMap;

  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;

  const goalsRef = useRef(goals);
  goalsRef.current = goals;

  const lifeAreasRef = useRef(lifeAreas);
  lifeAreasRef.current = lifeAreas;

  const renderItem: React.FC<RenderItemProps> = useMemo(
    () =>
      ({ item, itemInstance, tree, isMultiSelectModifierPressed }) => {
        const type = item.type;

        // Task
        if (type === TreeItemTypes.Task) {
          const currentTasksMap = tasksMapRef.current;
          const currentTasks = tasksRef.current;

          return currentTasksMap[item.sourceId] ? (
            <TreeTaskCard
              task={currentTasksMap[item.sourceId]}
              tree={tree}
              item={itemInstance}
              tasks={currentTasks}
              isMultiSelectModifierPressed={isMultiSelectModifierPressed}
              hideGoal
              displayDue={displayDue}
            />
          ) : null;
        }

        // New Task
        if (type === TreeItemTypes.NewTask) {
          const goalId = item.sourceId.replace('-new-task', '');
          const goal = goalId === goalInboxID ? undefined : (goalId as ID);
          return (
            <NewTaskRow
              goal={goal}
              date={defaultNewTaskDate}
              startTime={null}
              duration={null}
              lifeArea={groupSettings.lifeAreaId}
              tags={groupSettings.tagId ? [groupSettings.tagId] : undefined}
            />
          );
        }

        // Goal
        if (type === TreeItemTypes.Goal) {
          return (
            <GoalHeader
              item={itemInstance}
              goals={goalsRef.current}
              lifeAreas={lifeAreasRef.current}
              denseFormat={denseFormat}
            />
          );
        }

        return null;
      },
    [
      denseFormat,
      defaultNewTaskDate,
      groupSettings.lifeAreaId,
      groupSettings.tagId,
      displayDue,
    ],
  );

  const [tree] = useTree();
  const { submit: updateTasks } = useUpdateTasks();

  useTaskPriorityShortcuts(tree, tasks, updateTasks);
  useTaskDateShortcuts(tree, tasks, updateTasks);

  return (
    <Styled.Container>
      <VirtualizedTreeList
        rootItemId={rootId}
        items={treeItems}
        renderItem={renderItem}
        scrollElement={scrollElement}
        multiselectTypes={taskTreeItemTypes}
        dragTypes={canReorderGoals ? goalDragTypes : taskTreeItemTypes}
        onReorder={_onReorder}
        collapsedItems={collapsedSections}
        onCollapse={onSectionCollapse}
        denseFormat={denseFormat}
        isContextMenuOpen={isContextMenuOpen}
        dropTypes={dropTypes}
      />
    </Styled.Container>
  );
};
